import type { FC, ReactNode } from 'react'

import { useId, useState } from 'react'
import { ArrowRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Accent, SectionHeading } from '@/components/landing/section-heading'

import { faqs } from '@/constants/landing'

export const FaqSection: FC = (): ReactNode => {
    const [open, setOpen] = useState<number | null>(0)
    const baseId = useId().replace(/[^a-zA-Z0-9_-]/g, '')

    return (
        <section id='faq' className='scroll-mt-20 border-t border-hairline py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-20'>
                <Reveal>
                    <SectionHeading
                        index="07"
                        label="FAQ"
                        title={<>Questions, <Accent>answered.</Accent></>}
                        description="Everything you might want to know before your first experiment."
                    />

                    <a
                        href='#access'
                        className='group mt-8 inline-flex items-center gap-2 text-[15px] font-medium text-brand'
                    >
                        Still curious? Join the early-access list
                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                    </a>
                </Reveal>

                <Reveal delay={100}>
                    <ul className='divide-y divide-hairline border-y border-hairline'>
                        {faqs.map((faq, index) => {
                            const expanded = open === index
                            const buttonId = `${baseId}-question-${index}`
                            const panelId = `${baseId}-answer-${index}`

                            return (
                                <li key={faq.question}>
                                    <h3>
                                        <button
                                            id={buttonId}
                                            type='button'
                                            aria-expanded={expanded}
                                            aria-controls={panelId}
                                            onClick={() => setOpen(expanded ? null : index)}
                                            className='flex w-full items-center justify-between gap-6 py-6 text-left text-lg font-medium tracking-[-0.01em] text-ink'
                                        >
                                            {faq.question}

                                            <span
                                                className={cn(
                                                    'flex size-8 shrink-0 items-center justify-center rounded-full border border-border transition-all duration-300',
                                                    expanded ? 'rotate-45 border-ink bg-ink text-white' : 'text-ink'
                                                )}
                                            >
                                                <Plus className='size-4' />
                                            </span>
                                        </button>
                                    </h3>

                                    <div
                                        id={panelId}
                                        role='region'
                                        aria-labelledby={buttonId}
                                        className={cn(
                                            'grid transition-[grid-template-rows] duration-300 ease-out',
                                            expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                        )}
                                    >
                                        <div className='overflow-hidden' aria-hidden={!expanded}>
                                            <p className={cn('max-w-2xl pb-6 pr-12 text-[15px] leading-relaxed text-muted-foreground transition-opacity duration-300', expanded ? 'opacity-100' : 'opacity-0')}>
                                                {faq.answer}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </Reveal>
            </div>
        </section>
    )
}
